import {take} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {useToggle} from '../../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ConversationBaseModel, ConversationModel} from '../../../../../models/api/conversationModel';
import {TagModel} from '../../../../../models/api/tagModel';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {TopicModel} from '../../../../../models/api/topicModel';
import {CellsContext} from '../../../../../store/panels/cells/context/cellsContext';
import {InboxesVisualConversationSelection} from '../../../../../store/panels/cells/helpers/cellsPanelSelectionHelpers';
import {InboxesParameters} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {ViewerPanelId} from '../../../../../store/panels/panelStateBase';
import {ConversationHeaderOverflowButton} from './conversationHeaderOverflowButton';
import {ConversationHeaderSearch} from './conversationHeaderSearch';
import {ConversationSubjectWrapper} from './conversationSubjectWrapper';
import {ConversationHeaderTagPillContainer} from './tag/conversationHeaderTagPillContainer';
import {ConversationHeaderTopicContainer} from './topic/conversationHeaderTopicContainer';

/*
 * Constants.
 */

const collapsedHeaderPillsLimit = 10;

/*
 * Props.
 */

export interface ConversationHeaderProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  teammate: TeammateModel;
  conversationSelection: InboxesVisualConversationSelection;
  conversations: ReadonlyArray<ConversationBaseModel>;
  topics: ReadonlyArray<TopicModel>;
  tags: ReadonlyArray<TagModel>;
  onlyConversation: ConversationModel | undefined;
  isUserVisitor: boolean;
  canSearch: boolean;
  onSubjectChange: HandlerOf<string>;
  inboxesParameters: InboxesParameters;
}

/*
 * Style.
 */

const StyledHeaderDiv = styled.div`
  grid-area: header;
  background-color: ${(p) => p.theme.appBackground};
`;

const StyledContentDiv = styled.div`
  display: flex;
  padding: 6px 8px 6px 16px;
  padding-left: 2px;
  padding-right: 11px;
  min-height: 46px;
`;

const StyledLeftDiv = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-width: 0;
  position: relative;
`;

const StyledTagsDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 3px 0;
  padding-left: 8px;
`;

/*
 * Component.
 */

export const ConversationHeader: FC<ConversationHeaderProps> = (props) => {
  const [isExpanded, toggleExpand] = useToggle();
  const {cellsContext, conversations, onlyConversation, canSearch, onSubjectChange, panelId, isUserVisitor} =
    props;

  if (conversations.length === 0) {
    return null;
  }

  return (
    <StyledHeaderDiv>
      <StyledContentDiv>
        <StyledLeftDiv>
          {onlyConversation ? (
            <ConversationSubjectWrapper
              cellsContext={cellsContext}
              conversation={onlyConversation}
              panelId={panelId}
              onSubjectChange={onSubjectChange}
              isUserVisitor={isUserVisitor}
            />
          ) : null}
          {maybeRenderTopicsAndTags(props, isExpanded, toggleExpand)}
          {onlyConversation && canSearch ? <ConversationHeaderSearch key={onlyConversation.id} /> : null}
        </StyledLeftDiv>
      </StyledContentDiv>
    </StyledHeaderDiv>
  );
};

function maybeRenderTopicsAndTags(
  props: ConversationHeaderProps,
  isExpanded: boolean,
  onToggleExpandClick: Handler,
) {
  // We need at least one conversation to proceed.
  const {teammate, conversationSelection, topics, tags, isUserVisitor} = props;
  if (isUserVisitor) {
    return null;
  }

  const totalCount = topics.length + tags.length;
  if (totalCount === 0) {
    return null;
  }

  // Find how many pills we need to display.
  const limit = isExpanded ? Number.POSITIVE_INFINITY : collapsedHeaderPillsLimit;
  const overflowCount = totalCount - collapsedHeaderPillsLimit;

  // Filter the collections.
  const visibleTopics = take(topics, limit);
  const visibleTags = take(tags, limit - visibleTopics.length);
  const hasSinglePrivateTag = tags.filter((t) => t.namespace === teammate.namespace).length === 1;

  // Otherwise, render.
  return (
    <StyledTagsDiv>
      {visibleTopics.map((t) => (
        <ConversationHeaderTopicContainer
          key={t.externalLink}
          panelId={props.panelId}
          conversation={props.onlyConversation}
          teammateId={teammate.id}
          conversationSelection={conversationSelection}
          topic={t}
        />
      ))}
      {visibleTags.map((t) => (
        <ConversationHeaderTagPillContainer
          key={t.id}
          teammate={teammate}
          conversationSelection={conversationSelection}
          tag={t}
          isLastPrivateTag={t.namespace === teammate.namespace && hasSinglePrivateTag}
          panelId={props.panelId}
          inboxesParameters={props.inboxesParameters}
          cellsContext={props.cellsContext}
        />
      ))}
      {overflowCount > 0 && (
        <ConversationHeaderOverflowButton
          value={overflowCount}
          isExpanded={isExpanded}
          onClick={onToggleExpandClick}
        />
      )}
    </StyledTagsDiv>
  );
}
